import { createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  DOWNLOAD_LINK_TTL_MS,
  INTERRUPTED_MESSAGE,
  MAX_NOTIFICATIONS,
  NOTIFICATION_STATUS,
  OPEN_STATUSES,
} from './notificationsConstants';

/**
 * What a stored entry becomes when it is read back.
 *
 * Two things about the Centro de Actividad do not survive a closed tab, and
 * both would have the panel lying about them:
 *
 * - **A job that was still running.** Its closing event travelled on the socket
 *   while nobody was listening, and no producer sends a job id, so there is
 *   nothing to reconcile it against. It comes back as `INTERRUPTED` rather than
 *   as a spinner that never stops — and, because `selectIsKindRunning` only
 *   ever looks at `RUNNING`, a restored entry can never leave the button that
 *   launched it disabled either.
 * - **A download link older than a week.** The presigned URL is already dead;
 *   the entry stays, the button goes.
 *
 * @param {object} entry
 * @param {number} now
 */
export const restoreEntry = (entry, now = Date.now()) => {
  // Stalled and unconfirmed jobs were still waiting for an event, and that
  // event is just as lost as a running job's.
  if (OPEN_STATUSES.includes(entry?.status)) {
    return {
      ...entry,
      status: NOTIFICATION_STATUS.INTERRUPTED,
      message: INTERRUPTED_MESSAGE,
      url: null,
    };
  }

  if (entry?.url && now - (entry.updatedAt ?? 0) > DOWNLOAD_LINK_TTL_MS) {
    return { ...entry, url: null };
  }

  return entry;
};

/** @param {Array<object>} items */
export const restoreItems = (items, now = Date.now()) =>
  (Array.isArray(items) ? items : [])
    .map((entry) => restoreEntry(entry, now))
    .slice(0, MAX_NOTIFICATIONS);

const restoreTransform = createTransform(
  // Inbound: stored as it is. What the tab knows at that moment is true.
  (inboundState) => inboundState,
  (outboundState, key) => (key === 'items' ? restoreItems(outboundState) : outboundState),
  { whitelist: ['items'] }
);

/**
 * The slice has its own key, the way `deliveries`, `sellers` and `zones` each
 * keep their own: `persist:root` stays for the session alone, which is also the
 * key the end-to-end suite seeds to sign in.
 *
 * `isConnected` is left out on purpose: whether the socket is up is something
 * the connection says on load, never something to restore from disk.
 */
export const notificationsPersistConfig = {
  key: 'notifications',
  storage,
  whitelist: ['items'],
  transforms: [restoreTransform],
};
